import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../components/api/axiosConfig';
import { checkSession, redirectToLogin, getRedirectPath } from '../utils/sessionUtils';
import LoadingSpinner from '../components/loading/LoadingSpinner';
import SessionExpiredModal from '../components/sessionExpiry/SessionExpiredModal';

// Create context
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialCheckDone, setInitialCheckDone] = useState(false); // Track if initial auth check is complete
  const [error, setError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if the user is authenticated
  const checkAuthStatus = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const { isAuthenticated, user: sessionUser, error: sessionError } = await checkSession();

      if (isAuthenticated) {
        setUser(sessionUser);
        setError(null);
        return true;
      } else {
        setUser(null);
        if (sessionError) {
          setError(sessionError);
        }
        return false;
      }
    } catch (error) {
      setUser(null);
      setError('Session check failed');
      return false;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Login function
  const login = async (email, password, rememberMe = true) => {
    try {
      setLoading(true);
      setError(null);
      
      // Explicitly initialize the session to fetch a fresh CSRF token.
      // Crucial fix for Safari and aggressive ITP (Intelligent Tracking Prevention) 
      // preventing tokens during rapid double-logins.
      try {
        await axiosInstance.get('/api/init-session/', { withCredentials: true });
      } catch (e) {
        console.warn('Silent skip on CSRF init:', e.message);
      }
      
      const response = await axiosInstance.post('/api/login/', {
        email,
        password
      }, { withCredentials: true });
      
      // Store user data based on rememberMe preference
      if (rememberMe) {
        localStorage.setItem('user', JSON.stringify(response.data));
        sessionStorage.removeItem('user');
      } else {
        sessionStorage.setItem('user', JSON.stringify(response.data));
        localStorage.removeItem('user');
      }
      setUser(response.data);

      // Store organization IDs in sessionStorage for use in components
      if (response.data.profile && response.data.profile.npo) {
        sessionStorage.setItem('npo_id', response.data.profile.npo);
      }
      if (response.data.profile && response.data.profile.agency) {
        sessionStorage.setItem('agency_id', response.data.profile.agency);
      }
      if (response.data.profile && response.data.profile.team) {
        sessionStorage.setItem('team_id', response.data.profile.team);
      }

      // Get the redirect path from URL or use default, with permission checking
      const redirectPath = getRedirectPath(location, response.data.user_group);
      navigate(redirectPath);

      return response.data;
    } catch (error) {
      setError(error.response?.data?.detail || 'An error occurred during login');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setLoading(true);
      
      // Clear all stored session data first to ensure the UI updates immediately
      setUser(null);
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('user_group');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_id');
      sessionStorage.removeItem('npo_id');
      sessionStorage.removeItem('agency_id');
      sessionStorage.removeItem('team_id');
      
      // Then attempt the API call
      try {
        await axiosInstance.post('/api/logout/', {}, { withCredentials: true });
        console.log('Logout API call successful');
      } catch (apiError) {
        // If API call fails, we already cleared the data, so just log the error
        console.error('Logout API error:', apiError.message);
      }
      
      // Always navigate to login with a HARD RELOAD to refresh CSRF Token
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      setError('An error occurred during logout');
      
      // Ensure session data is cleared even if there's an error
      setUser(null);
      localStorage.removeItem('user');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('user_group');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_id');
      
      // Always redirect to login with a HARD RELOAD to refresh CSRF Token
      window.location.href = '/login';
    } finally {
      setLoading(false);
    }
  };

  // Check for stored user data on initial load
  useEffect(() => {
    // Prefer sessionStorage (non-remember) over localStorage
    const sessionUser = sessionStorage.getItem('user');
    const localUser = localStorage.getItem('user');
    const storedUser = sessionUser || localUser;
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);

        // Also ensure sessionStorage is in sync with localStorage
        // This helps when opening a new tab directly
        if (userData.user_group) sessionStorage.setItem('user_group', userData.user_group);
        if (userData.access) sessionStorage.setItem('access_token', userData.access);
        if (userData.access_token) sessionStorage.setItem('access_token', userData.access_token);

        // Store user_id from wherever it's available
        if (userData.user_id) {
          sessionStorage.setItem('user_id', userData.user_id);
        } else if (userData.id) {
          sessionStorage.setItem('user_id', userData.id);
        } else if (userData.user && userData.user.id) {
          sessionStorage.setItem('user_id', userData.user.id);
        }

        // Store organization IDs for NPO managers, agency managers, and team managers
        if (userData.profile && userData.profile.npo) {
          sessionStorage.setItem('npo_id', userData.profile.npo);
        }
        if (userData.profile && userData.profile.agency) {
          sessionStorage.setItem('agency_id', userData.profile.agency);
        }
        if (userData.profile && userData.profile.team) {
          sessionStorage.setItem('team_id', userData.profile.team);
        }
      } catch (e) {
        // Invalid stored data
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
      }
    }
    setLoading(false);
    setInitialCheckDone(true); // Mark initial check as complete
  }, []);

  // Check authentication status when the app loads or route changes
  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check for public pages
      const publicPaths = ['/login', '/sign-up', '/', '/set-password', '/forgot-password', '/privacy', '/terms', '/cookies', '/contact'];
      const isPublicPath = publicPaths.includes(location.pathname) || location.pathname.startsWith('/public/');
      if (isPublicPath) {
        setLoading(false);
        return;
      }

      // Don't show loading on route changes - only on initial check
      const isAuthenticated = await checkAuthStatus(false);

      if (!isAuthenticated && !isPublicPath) {
        // Use redirectToLogin utility to preserve the intended destination
        redirectToLogin(navigate, location.pathname);
      }
    };

    checkAuth();
  }, [location.pathname, navigate]);
  
  // Set up a periodic check to detect session expiry
  useEffect(() => {
    // Only set up the check if the user is logged in
    if (!user) return;
    
    // Check every 5 minutes
    const intervalId = setInterval(async () => {
      const isAuthenticated = await checkAuthStatus();
      
      // If session expired, show session expired modal instead of immediate redirect
      const publicPaths = ['/login', '/sign-up', '/', '/set-password', '/forgot-password', '/privacy', '/terms', '/cookies', '/contact'];
      const isPublicPath = publicPaths.includes(location.pathname) || location.pathname.startsWith('/public/');
      if (!isAuthenticated && !isPublicPath) {
        setSessionExpired(true);
      }
    }, 5 * 60 * 1000); // 5 minutes in milliseconds
    
    return () => clearInterval(intervalId);
  }, [user, location.pathname]);
  
  // Listen for localStorage changes from other tabs
  useEffect(() => {
    // Function to handle storage changes
    const handleStorageChange = (event) => {
      // If user data was changed or removed in another tab
      if (event.key === 'user') {
        if (event.newValue === null) {
          // User was logged out in another tab
          setUser(null);
          sessionStorage.removeItem('user_group');
          sessionStorage.removeItem('access_token');
          sessionStorage.removeItem('user_id');
          
          // Redirect to login if not on a public page
          const publicPaths = ['/login', '/sign-up', '/', '/set-password', '/forgot-password', '/privacy', '/terms', '/cookies', '/contact'];
          const isPublicPath = publicPaths.includes(location.pathname) || location.pathname.startsWith('/public/');
          if (!isPublicPath) {
            navigate('/login');
          }
        } else if (event.newValue) {
          // User data was updated in another tab
          try {
            const userData = JSON.parse(event.newValue);
            setUser(userData);
          } catch (e) {
            console.error('Error parsing user data from storage event:', e);
          }
        }
      }
    };
    
    // Add event listener for storage events
    window.addEventListener('storage', handleStorageChange);
    
    // Clean up the event listener
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [location.pathname, navigate]);
  
  // Handle closing the session expired modal
  const handleCloseSessionModal = () => {
    setSessionExpired(false);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {loading && !initialCheckDone && !['/login', '/sign-up', '/privacy', '/terms', '/cookies', '/contact'].includes(location.pathname) && !location.pathname.startsWith('/public/') && (
        <LoadingSpinner message="Checking your session..." />
      )}
      <SessionExpiredModal
        isOpen={sessionExpired}
        onClose={handleCloseSessionModal}
        currentPath={location.pathname}
      />
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;